import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useGetQuotations } from "api/queries/quotes/useGetQuotations";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import RequestApprovalDialog from "pages/shipper/ShipmentQuotes/components/RequestApprovalDialog";
import ConfirmBookQuoteDialog from "pages/shipper/ShipmentQuotes/components/ConfirmBookQuoteDialog";
import ManageApprovalDialog from "pages/shipper/ShipmentQuotes/components/ManageApprovalDialog";
import TransferApprovalDialog from "pages/shipper/ShipmentQuotes/components/TransferApprovalDialog";
import ManualParcelQuoteDialog from "pages/shipper/ShipmentQuotes/components/ManualParcelQuoteDialog";
import CancelManualParcelQuoteDialog from "pages/shipper/ShipmentQuotes/components/CancelManualParcelQuoteDialog";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { useStateMachine } from "little-state-machine";
import { updateBackgroundLoadingState } from "store/store";

export type HandleClickRequestApprovalParams = {
  isAdditionalApproval?: boolean;
  isSuggestApproval?: boolean;
};

interface useRequestApprovalHelperProps {
  quotation: any;
  showManageApprovalButton: boolean;
  showActiveActionButtons: boolean;
}

export default function useBookingApprovalFlow({
  quotation,
  showManageApprovalButton,
  showActiveActionButtons,
}: useRequestApprovalHelperProps) {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });

  const { reference, status, general, schedule, financial, provider, created_by_user_type } = quotation ?? {};
  const isQuotationExpired = general.quote_expiration_seconds_remaining < 0;

  // Dialog states
  const [showBookQuoteDialog, setShowBookQuoteDialog] = useState(false);
  const [showRequestApprovalDialog, setShowRequestApprovalDialog] = useState(false);
  const [showManageApprovalDialog, setShowManageApprovalDialog] = useState(false);
  const [showTransferApprovalDialog, setShowTransferApprovalDialog] = useState(false);

  // Manual parcen states
  const [showManualQuoteDialog, setShowManualQuoteDialog] = useState(false);
  const [showCancelManualQuoteDialog, setShowCancelManualQuoteDialog] = useState(false);

  const [isAdditionalApproval, setIsAdditionalApproval] = useState(false);
  const [isSuggestApproval, setIsSuggestApproval] = useState(false);

  const [bookingPayload, setBookingPayload] = useState<any>(null);
  const [bookingType, setBookingType] = useState<"direct_book" | "request_approval" | "book_as_duplicate">("direct_book");
  const [showCustomsBroker, setShowCustomsBroker] = useState(false);

  const fetchQuoteRequestData = () => {
    setShowRequestApprovalDialog(false);
    actions.updateBackgroundLoadingState(true);
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteDetails(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentQuotationActionHistoryDetails(id!) });
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(id!) });
  };

  const { data: quotationsData } = useGetQuotations({
    shipmentId: id!,
  });

  const quotationSettings = quotationsData?.settings;
  const { booked_as_duplicate, customs_broker_available } = quotationSettings || {};

  const openTransferApprovalDialog = () => {
    setShowTransferApprovalDialog(true);
  };

  const handleClickRequestApproval = ({
    isAdditionalApproval = false,
    isSuggestApproval = false,
  }: HandleClickRequestApprovalParams) => {
    setShowCustomsBroker(customs_broker_available);
    setIsAdditionalApproval(isAdditionalApproval);
    setIsSuggestApproval(isSuggestApproval);
    setShowRequestApprovalDialog(true);
  };

  const handleClickManageApproval = () => {
    setShowCustomsBroker(customs_broker_available);
    setShowManageApprovalDialog(true);
  };

  const handleClickBook = () => {
    setShowCustomsBroker(customs_broker_available);
    setShowBookQuoteDialog(true);
  };

  const handleClickEditManualQuote = () => {
    setShowManualQuoteDialog(true);
  };

  const handleClickCancelManualQuote = () => {
    setShowCancelManualQuoteDialog(true);
  };

  const editQuoteData = {
    reference,
    tracking_number: general.tracking_number,
    pickup_date: schedule.quoted_pickup_date,
    pickup_time: schedule.quoted_pickup_date,
    delivery_date: schedule.quoted_time_delivery_date,
    delivery_time: schedule.quoted_time_delivery_date,
    value: parseInt(financial.total_quoted_amount_lsp),
    currency_code: financial.lsp_currency,
  };

  const renderApprovalFlowDialogs = () => (
    <>
      <RequestApprovalDialog
        isOpen={showRequestApprovalDialog}
        close={() => {
          setShowBookQuoteDialog(false);
          setShowRequestApprovalDialog(false);
        }}
        quotationId={reference}
        isBookAsDuplicate={booked_as_duplicate}
        setShowBookQuoteDialog={setShowBookQuoteDialog}
        setBookingPayload={setBookingPayload}
        isAdditionalApproval={isAdditionalApproval}
        isSuggestApproval={isSuggestApproval}
        setBookingType={setBookingType}
        fetchQuoteRequestData={fetchQuoteRequestData}
        status={status}
      />

      <ManageApprovalDialog
        isOpen={showManageApprovalDialog}
        setShowConfirmBookQuoteDialog={setShowBookQuoteDialog}
        close={() => {
          setShowManageApprovalDialog(false);
          setShowBookQuoteDialog(false);
        }}
        openTransferApprovalDialog={openTransferApprovalDialog}
        shipmentId={id!}
        quotationId={reference}
        fetchQuoteRequestData={fetchQuoteRequestData}
        quotationSettings={quotationSettings}
        setBookingPayload={setBookingPayload}
        setBookingType={setBookingType}
        setShowBookQuoteDialog={setShowBookQuoteDialog}
        status={status}
        isQuotationExpired={isQuotationExpired}
      />

      {showBookQuoteDialog && (
        <ConfirmBookQuoteDialog
          close={() => {
            setShowCustomsBroker(false);
            setShowBookQuoteDialog(false);
          }}
          quotationId={reference}
          isApprovalRequested={showActiveActionButtons && showManageApprovalButton}
          bookingType={bookingType}
          requestData={bookingPayload}
          isBookAsDuplicate={booked_as_duplicate}
          fetchQuoteRequestData={fetchQuoteRequestData}
          show={showBookQuoteDialog}
          setShowBookQuoteDialog={setShowBookQuoteDialog}
          shipmentId={id!}
          showCustomsBroker={showCustomsBroker}
          setShowCustomsBroker={setShowCustomsBroker}
          quotationSettings={quotationSettings}
          customBrokerAvailable={customs_broker_available}
          status={status}
          isFedexPostmenQuotation={general?.is_fedex_postmen_quotation}
          isFedexDirectIntegration={general?.is_fedex_direct_integration}
        />
      )}

      <TransferApprovalDialog
        isOpen={showTransferApprovalDialog}
        close={() => setShowTransferApprovalDialog(false)}
        setBookingPayload={setBookingPayload}
        openManageApprovalDialog={() => setShowManageApprovalDialog(true)}
        setShowBookQuoteDialog={setShowBookQuoteDialog}
        setBookingType={setBookingType}
        quotationId={reference}
        shipmentId={id!}
        status={status}
      />

      <ManualParcelQuoteDialog
        isOpen={showManualQuoteDialog}
        close={() => {
          setShowManualQuoteDialog(false);
          setShowBookQuoteDialog(false);
        }}
        editManualQuoteData={editQuoteData}
        fetchQuoteRequestData={fetchQuoteRequestData}
        isParcel={provider?.is_parcel_company}
        isManualQuote={created_by_user_type === "shipper"}
      />

      <CancelManualParcelQuoteDialog
        isOpen={showCancelManualQuoteDialog}
        close={() => {
          setShowCancelManualQuoteDialog(false);
          setShowBookQuoteDialog(false);
        }}
        quotation={quotation}
      />
    </>
  );

  return {
    handleClickRequestApproval,
    handleClickManageApproval,
    handleClickBook,
    handleClickEditManualQuote,
    handleClickCancelManualQuote,
    renderApprovalFlowDialogs,
  };
}
